/**
 * Save storage (spec §10, §11): one text value (the record's JSON) per slot. IndexedDB first (database
 * `eclipse`, store `saves`, keyed by slot); when it is unavailable (private mode) localStorage (one key per slot
 * under `localStorageKey`), and the UI shows the persistent banner `STORAGE_BANNER`; when neither works an
 * in-memory store keeps the session playable (kind `none`, nothing persists).
 *
 * Values are stored as text so a corrupt record is read back byte for byte, reported and exported as it is.
 *
 * M4 (plan I2, §6): a companion store beside the saves (IndexedDB store `strategic`, database version 2;
 * localStorage keys `<localStorageKey>.strategic.<key>` plus the key index `<localStorageKey>.strategic`). A
 * `SaveCompanion` registered with `setSaveCompanion` sees every `put`: it may add one companion entry that is
 * written in the same transaction as the slot text, and cleans up after the write. The slot text itself is never
 * changed, so M2's record format and size cap are untouched.
 */
import { SAVE_SLOTS, type SaveSlotId, type StorageKind } from '../ui/contracts'
import { logger } from '../core/logger'

export const STORAGE_BANNER = 'Progress may not persist in this browser mode.'

export interface SaveStorage {
  readonly kind: StorageKind
  /** The stored text, or null for an empty slot. */
  get(slot: SaveSlotId): Promise<string | null>
  put(slot: SaveSlotId, text: string): Promise<void>
  delete(slot: SaveSlotId): Promise<void>
  /** Slots that hold a value, in `SAVE_SLOTS` order. */
  list(): Promise<SaveSlotId[]>
  /** M4 (plan I2): the companion store beside the saves. */
  readonly companion?: CompanionStore
}

/** The companion values beside the saves (M4 plan I2: the strategic saves), keyed by text. */
export interface CompanionStore {
  get(key: string): Promise<string | null>
  /** Writes the slot text and the companion value together (one IndexedDB transaction). */
  putWith(slot: SaveSlotId, text: string, key: string, value: string): Promise<void>
  delete(key: string): Promise<void>
  keys(): Promise<string[]>
}

/** Hooks every slot write (M4 plan I2: the strategic save bridge). */
export interface SaveCompanion {
  /** Before the write: the companion entry to write with this slot text, or null to write the slot alone. */
  beforePut(slot: SaveSlotId, text: string): Promise<{ key: string; value: string } | null>
  /** After a successful write (cleanup of entries no slot points to any more). */
  afterPut(storage: SaveStorage): Promise<void>
}

let saveCompanion: SaveCompanion | null = null

/** Registers (or clears) the companion. Every storage checks it at each `put`, so the order against opening does not matter. */
export function setSaveCompanion(c: SaveCompanion | null): void {
  saveCompanion = c
}

/** The IndexedDB object store (and localStorage key prefix) that holds the companion values. */
export const COMPANION_STORE = 'strategic'

/** A `put` through the registered companion (if any). A failing companion never fails the slot write. */
async function putThroughCompanion(
  storage: SaveStorage,
  plain: (slot: SaveSlotId, text: string) => Promise<void>,
  slot: SaveSlotId,
  text: string,
): Promise<void> {
  const c = saveCompanion
  const store = storage.companion
  if (c === null || store === undefined) return plain(slot, text)
  let entry: { key: string; value: string } | null = null
  try {
    entry = await c.beforePut(slot, text)
  } catch (err) {
    logger.warn('save: the companion could not prepare its entry; saving the slot alone', err)
  }
  if (entry === null) {
    await plain(slot, text)
  } else {
    try {
      await store.putWith(slot, text, entry.key, entry.value)
    } catch (err) {
      logger.warn('save: the companion write failed; saving the slot alone', err)
      await plain(slot, text)
    }
  }
  try {
    await c.afterPut(storage)
  } catch (err) {
    logger.warn('save: the companion cleanup failed', err)
  }
}

export interface SaveStorageConfig {
  dbName: string
  store: string
  localStorageKey: string
}

/** The part of the Web Storage API the fallback uses. */
export interface StorageLike {
  getItem(key: string): string | null
  setItem(key: string, value: string): void
  removeItem(key: string): void
}

export interface SaveStorageEnv {
  indexedDB?: IDBFactory
  localStorage?: StorageLike
}

function isSlot(key: unknown): key is SaveSlotId {
  return typeof key === 'string' && (SAVE_SLOTS as readonly string[]).includes(key)
}

function inSlotOrder(keys: readonly SaveSlotId[]): SaveSlotId[] {
  return SAVE_SLOTS.filter((s) => keys.includes(s))
}

// ---------------------------------------------------------------------------------------------
// IndexedDB
// ---------------------------------------------------------------------------------------------

function requestToPromise<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = (): void => resolve(request.result)
    request.onerror = (): void => reject(request.error ?? new Error('IndexedDB request failed'))
  })
}

function transactionDone(tx: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    tx.oncomplete = (): void => resolve()
    tx.onerror = (): void => reject(tx.error ?? new Error('IndexedDB transaction failed'))
    tx.onabort = (): void => reject(tx.error ?? new Error('IndexedDB transaction aborted'))
  })
}

/** Database version 2 (M4 plan I2) adds the companion store; the upgrade creates whichever store is missing. */
const DB_VERSION = 2

function openDatabase(factory: IDBFactory, dbName: string, store: string): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = factory.open(dbName, DB_VERSION)
    request.onupgradeneeded = (): void => {
      if (!request.result.objectStoreNames.contains(store)) request.result.createObjectStore(store)
      if (!request.result.objectStoreNames.contains(COMPANION_STORE)) request.result.createObjectStore(COMPANION_STORE)
    }
    request.onsuccess = (): void => resolve(request.result)
    request.onerror = (): void => reject(request.error ?? new Error('IndexedDB open failed'))
    request.onblocked = (): void => reject(new Error('IndexedDB open blocked'))
  })
}

/** Opens (and creates on first use) the saves store. Rejects when IndexedDB is unusable. */
export async function openIndexedDbStorage(factory: IDBFactory, dbName: string, store: string): Promise<SaveStorage> {
  const db = await openDatabase(factory, dbName, store)
  const run = <T>(mode: IDBTransactionMode, op: (s: IDBObjectStore) => IDBRequest<T>, name: string = store): Promise<T> =>
    requestToPromise(op(db.transaction(name, mode).objectStore(name)))
  const plainPut = async (slot: SaveSlotId, text: string): Promise<void> => {
    await run('readwrite', (s) => s.put(text, slot))
  }
  const companion: CompanionStore = {
    async get(key) {
      const value: unknown = await run('readonly', (s) => s.get(key), COMPANION_STORE)
      return typeof value === 'string' ? value : null
    },
    async putWith(slot, text, key, value) {
      const tx = db.transaction([store, COMPANION_STORE], 'readwrite')
      const done = transactionDone(tx)
      tx.objectStore(COMPANION_STORE).put(value, key)
      tx.objectStore(store).put(text, slot)
      await done
    },
    async delete(key) {
      await run('readwrite', (s) => s.delete(key), COMPANION_STORE)
    },
    async keys() {
      const keys = await run('readonly', (s) => s.getAllKeys(), COMPANION_STORE)
      return keys.filter((k): k is string => typeof k === 'string')
    },
  }
  const storage: SaveStorage = {
    kind: 'indexeddb',
    companion,
    async get(slot) {
      const value: unknown = await run('readonly', (s) => s.get(slot))
      return typeof value === 'string' ? value : value === undefined ? null : JSON.stringify(value)
    },
    put: (slot, text) => putThroughCompanion(storage, plainPut, slot, text),
    async delete(slot) {
      await run('readwrite', (s) => s.delete(slot))
    },
    async list() {
      const keys = await run('readonly', (s) => s.getAllKeys())
      return inSlotOrder(keys.filter(isSlot))
    },
  }
  return storage
}

// ---------------------------------------------------------------------------------------------
// localStorage and memory
// ---------------------------------------------------------------------------------------------

/**
 * One key per slot: `<localStorageKey>.<slot>`. Quota errors reject the `put`. The companion values sit under
 * `<localStorageKey>.strategic.<key>` and their key list under `<localStorageKey>.strategic` (`StorageLike` has no
 * key listing).
 */
export function createLocalStorageStorage(ls: StorageLike, localStorageKey: string, kind: StorageKind = 'localstorage'): SaveStorage {
  const keyOf = (slot: SaveSlotId): string => `${localStorageKey}.${slot}`
  const indexKey = `${localStorageKey}.${COMPANION_STORE}`
  const companionKeyOf = (key: string): string => `${indexKey}.${key}`
  const readIndex = (): string[] => {
    const raw = ls.getItem(indexKey)
    if (raw === null) return []
    try {
      const parsed: unknown = JSON.parse(raw)
      return Array.isArray(parsed) ? parsed.filter((k): k is string => typeof k === 'string') : []
    } catch {
      return []
    }
  }
  const plainPut = async (slot: SaveSlotId, text: string): Promise<void> => {
    ls.setItem(keyOf(slot), text)
  }
  const companion: CompanionStore = {
    get: (key) => Promise.resolve(ls.getItem(companionKeyOf(key))),
    async putWith(slot, text, key, value) {
      ls.setItem(companionKeyOf(key), value)
      const index = readIndex()
      if (!index.includes(key)) ls.setItem(indexKey, JSON.stringify([...index, key]))
      ls.setItem(keyOf(slot), text)
    },
    async delete(key) {
      ls.removeItem(companionKeyOf(key))
      const index = readIndex()
      if (index.includes(key)) ls.setItem(indexKey, JSON.stringify(index.filter((k) => k !== key)))
    },
    keys: () => Promise.resolve(readIndex()),
  }
  const storage: SaveStorage = {
    kind,
    companion,
    get: (slot) => Promise.resolve(ls.getItem(keyOf(slot))),
    put: (slot, text) => putThroughCompanion(storage, plainPut, slot, text),
    delete: async (slot) => {
      ls.removeItem(keyOf(slot))
    },
    list: () => Promise.resolve(SAVE_SLOTS.filter((s) => ls.getItem(keyOf(s)) !== null)),
  }
  return storage
}

/** A Map-backed `StorageLike` (the `none` fallback, and tests). */
export function createMemoryStorageLike(): StorageLike & { readonly size: number } {
  const map = new Map<string, string>()
  return {
    getItem: (key) => map.get(key) ?? null,
    setItem: (key, value) => {
      map.set(key, value)
    },
    removeItem: (key) => {
      map.delete(key)
    },
    get size() {
      return map.size
    },
  }
}

function usableLocalStorage(ls: StorageLike | undefined, key: string): boolean {
  if (ls === undefined) return false
  try {
    const probe = `${key}.__probe`
    ls.setItem(probe, '1')
    ls.removeItem(probe)
    return true
  } catch {
    return false
  }
}

function defaultEnv(): SaveStorageEnv {
  const env: SaveStorageEnv = {}
  try {
    if (typeof indexedDB !== 'undefined') env.indexedDB = indexedDB
  } catch {
    // Some browsers throw on access in private mode.
  }
  try {
    if (typeof localStorage !== 'undefined') env.localStorage = localStorage
  } catch {
    // Access can throw when storage is blocked.
  }
  return env
}

export interface OpenedStorage {
  storage: SaveStorage
  kind: StorageKind
}

/** IndexedDB → localStorage → memory. `env` defaults to the browser globals. */
export async function openSaveStorage(config: SaveStorageConfig, env: SaveStorageEnv = defaultEnv()): Promise<OpenedStorage> {
  if (env.indexedDB !== undefined) {
    try {
      const storage = await openIndexedDbStorage(env.indexedDB, config.dbName, config.store)
      return { storage, kind: storage.kind }
    } catch (err) {
      logger.warn('save: IndexedDB unavailable, falling back to localStorage', err)
    }
  }
  if (env.localStorage !== undefined && usableLocalStorage(env.localStorage, config.localStorageKey)) {
    return { storage: createLocalStorageStorage(env.localStorage, config.localStorageKey), kind: 'localstorage' }
  }
  logger.warn('save: no persistent storage available; saves last only for this session')
  return { storage: createLocalStorageStorage(createMemoryStorageLike(), config.localStorageKey, 'none'), kind: 'none' }
}
